import { styled } from '@mui/material/styles'
import Box from '@/components/common/Box'
import { colors, typography, spacing, radius, sizing, focusVisible } from '@/styles/designTokens'

export const ItemCard = styled(Box)({
	position: 'relative',
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'flex-start',
	gap: 0,
	padding: spacing.xl,
	cursor: 'pointer',
	'&:hover': {
		backgroundColor: colors.overlaySubtle,
	},
})

// Full-cover button overlay — provides keyboard and AT access for the card-edit action
// without nesting a <button> inside role="button". Sits at z-index:0 behind all flex
// children (ItemImage, ItemDetails, ItemActions) which are at z-index:1.
export const ItemCardEditBtn = styled('button')({
	position: 'absolute',
	inset: 0,
	background: 'none',
	border: 'none',
	cursor: 'pointer',
	zIndex: 0,
	fontFamily: 'inherit',
	'&:focus-visible': {
		outline: `${focusVisible.outline} ${colors.brand}`,
		outlineOffset: focusVisible.outlineOffset,
	},
})

export const ItemImage = styled('img')({
	zIndex: 1,
	width: sizing.cartItemImage,
	height: sizing.cartItemImage,
	borderRadius: radius.card,
	objectFit: 'cover',
	flexShrink: 0,
	marginRight: spacing.lg,
})

export const ItemImagePlaceholder = styled(Box)({
	zIndex: 1,
	width: sizing.cartItemImage,
	height: sizing.cartItemImage,
	borderRadius: radius.card,
	backgroundColor: colors.overlayLight,
	flexShrink: 0,
	marginRight: spacing.lg,
})

export const ItemDetails = styled(Box)({
	zIndex: 1,
	flex: '1 1 auto',
	minWidth: 0,
	display: 'flex',
	flexDirection: 'column',
	gap: spacing.xs,
	paddingBottom: spacing.md,
	marginRight: spacing.sm,
})

export const ItemNameGroup = styled(Box)({
	display: 'flex',
	flexDirection: 'column',
	gap: spacing.xs,
})

export const ItemName = styled('span')({
	fontSize: typography.fontSizeMd,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	color: colors.textPrimary,
	overflowWrap: 'break-word',
})

export const ItemModifiers = styled('span')({
	fontSize: typography.fontSizeSm,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightSm,
	letterSpacing: typography.letterSpacingWidest,
	color: colors.textSecondary,
	overflowWrap: 'break-word',
})

export const ItemPrice = styled('span')({
	fontSize: typography.fontSizeMd,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	color: colors.textPrimary,
})

const PILL_COLLAPSED = sizing.iconButton // 40
const PILL_EXPANDED = 120 // 3× button width
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)'
const DURATION_EXPAND = '240ms'
const DURATION_LEAD = '160ms'

export const QTY_EXPAND_SHELL_CLASS = 'QtyExpandShell'
export const QTY_EXPAND_TRACK_CLASS = 'QtyExpandTrack'
export const QTY_EXPAND_BOX_CLASS = 'QtyExpandBox'
export const QTY_EXPAND_TRASH_OUTSIDE_CLASS = 'QtyExpandTrashOutside'
export const QTY_EXPAND_CLASS = 'QtyExpand'

// ItemActions is the :has() root for the trash-hide animation.
// Both selectors scope to QtyExpandShell — not QtyExpand — so that tabbing
// to QtyExpandTrashOutside (which is inside QtyExpand but outside the shell)
// does not trigger the pill expand or hide the outside trash.
export const ItemActions = styled(Box)({
	zIndex: 1,
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	gap: spacing.sm,
	flexShrink: 0,

	'@media (prefers-reduced-motion: reduce)': {
		[`& .${QTY_EXPAND_SHELL_CLASS}`]: { transition: 'none' },
		[`& .${QTY_EXPAND_TRACK_CLASS}`]: { transition: 'none' },
		[`& .${QTY_EXPAND_BOX_CLASS}`]: { transition: 'none' },
		[`& .${QTY_EXPAND_TRASH_OUTSIDE_CLASS}`]: { transition: 'none' },
	},

	// Hide the outside trash and collapse the gap when the pill expands.
	// Triggered by shell hover OR by focus landing inside the shell (not the outside trash).
	[`&:has(.${QTY_EXPAND_SHELL_CLASS}:hover), &:has(.${QTY_EXPAND_SHELL_CLASS}:focus-within)`]: {
		gap: 0,

		[`& .${QTY_EXPAND_TRASH_OUTSIDE_CLASS}`]: {
			opacity: 0,
			width: 0,
			pointerEvents: 'none',
		},
	},
})

const BaseButton = styled('button')({
	fontFamily: 'inherit',
	background: 'none',
	border: 'none',
	padding: 0,
	cursor: 'pointer',
})

export const QtyExpand = styled(Box)({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	height: PILL_COLLAPSED,

	// Expand the pill when hovering the shell or when a button inside the shell is focused.
	// QtyExpandTrashOutside is also a child of QtyExpand — scoping to QtyExpandShell:focus-within
	// prevents the outside trash receiving focus from triggering the pill expand.
	[`&:has(.${QTY_EXPAND_SHELL_CLASS}:hover), &:has(.${QTY_EXPAND_SHELL_CLASS}:focus-within)`]: {
		[`& .${QTY_EXPAND_SHELL_CLASS}`]: {
			width: PILL_EXPANDED,
			boxShadow: `inset 0 0 0 1px ${colors.border}`,
			borderRadius: radius.badge,
		},
		[`& .${QTY_EXPAND_TRACK_CLASS}`]: {
			transform: 'translate3d(0, 0, 0)',
		},
		[`& .${QTY_EXPAND_BOX_CLASS}`]: {
			boxShadow: 'none',
			borderRadius: 0,
		},
	},
})

// Rendered as a div, not a button — this is intentionally mouse-only (AT users reach the
// same action via the keyboard-accessible lead button inside the pill). A div does not
// receive focus on click, so aria-hidden="true" on the JSX side is unambiguous.
export const QtyExpandTrashOutside = styled('div')({
	flexShrink: 0,
	width: PILL_COLLAPSED,
	height: PILL_COLLAPSED,
	display: 'grid',
	placeItems: 'center',
	color: colors.textSecondary,
	borderRadius: radius.badge,
	opacity: 1,
	cursor: 'pointer',
	transition: `opacity ${DURATION_LEAD} ease, width ${DURATION_EXPAND} ${EASING}`,
	'&:hover': {
		backgroundColor: colors.overlayLight,
	},
})

export const QtyExpandShell = styled(Box)({
	flexShrink: 0,
	overflow: 'hidden',
	width: PILL_COLLAPSED,
	height: PILL_COLLAPSED,
	borderRadius: radius.badge,
	boxShadow: `inset 0 0 0 1px ${colors.border}`,
	backgroundColor: colors.surfaceWhite,
	transition: `width ${DURATION_EXPAND} ${EASING}, box-shadow ${DURATION_EXPAND} ${EASING}, border-radius ${DURATION_EXPAND} ${EASING}`,
})

export const QtyExpandTrack = styled(Box)({
	display: 'flex',
	flexDirection: 'row',
	width: PILL_EXPANDED,
	height: PILL_COLLAPSED,
	transform: `translate3d(-${PILL_COLLAPSED}px, 0, 0)`,
	transition: `transform ${DURATION_EXPAND} ${EASING}`,
})

export const QtyExpandLead = styled(Box)({
	position: 'relative',
	flexShrink: 0,
	width: PILL_COLLAPSED,
	height: PILL_COLLAPSED,
})

interface QtyExpandBoxProps {
	readOnly?: boolean
}

export const QtyExpandBox = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'readOnly',
})<QtyExpandBoxProps>(({ readOnly }) => ({
	flexShrink: 0,
	width: PILL_COLLAPSED,
	height: PILL_COLLAPSED,
	display: 'grid',
	placeItems: 'center',
	fontSize: typography.fontSizeMd,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	color: readOnly ? colors.textDisabled : colors.textPrimary,
	userSelect: 'none',
	// readOnly: surfaceLight bg + outline border per Figma (surface/surfacelight + surface/outline)
	// interactive: no background (pill provides it on expand)
	backgroundColor: readOnly ? colors.surfaceMuted : colors.borderTransparent,
	border: readOnly ? `1px solid ${colors.border}` : 'none',
	borderRadius: readOnly ? radius.badge : radius.none,
	boxShadow: 'none',
	transition: `box-shadow ${DURATION_EXPAND} ${EASING}, border-radius ${DURATION_EXPAND} ${EASING}`,
}))

export const ModifierToggleBtn = styled(BaseButton)({
	display: 'inline',
	fontSize: typography.fontSizeSm,
	fontWeight: typography.fontWeightSemiBold,
	lineHeight: typography.lineHeightSm,
	letterSpacing: typography.letterSpacingWidest,
	color: colors.textPrimary,
	textAlign: 'left',
	'&:focus-visible': {
		outline: `${focusVisible.outline} ${colors.brand}`,
		outlineOffset: focusVisible.outlineOffset,
	},
})

export const CartDivider = styled('hr')({
	margin: 0,
	border: 'none',
	borderTop: `1px solid ${colors.divider}`,
})

export const QtyExpandBtn = styled(BaseButton)({
	flexShrink: 0,
	width: PILL_COLLAPSED,
	height: PILL_COLLAPSED,
	display: 'grid',
	placeItems: 'center',
	color: colors.textSecondary,
	borderRadius: radius.badge,
	'&:hover': {
		backgroundColor: colors.overlayLight,
	},
	'&:focus-visible': {
		outline: `${focusVisible.outline} ${colors.brand}`,
		// Inset outline — renders inside the QtyExpandShell overflow:hidden boundary
		outlineOffset: -focusVisible.outlineOffset,
	},
})
